import { isSameUsScope } from '@/lib/polymarketUs/portfolioModel';
import { formatUsUsd, formatUsSignedUsd } from '@/lib/polymarketUs/format';
import type { UsTrackerRead } from './usTrackerFeed';

const SCALE = 1_000_000_000_000n;
const units = (text: string): bigint => {
  const [whole, fraction = ''] = text.replace(/^-/, '').split('.');
  return (BigInt(whole) * SCALE + BigInt(fraction.padEnd(12, '0'))) * (text.startsWith('-') ? -1n : 1n);
};
const decimal = (value: bigint): string => {
  const abs = value < 0n ? -value : value;
  const fraction = (abs % SCALE).toString().padStart(12, '0').replace(/0+$/, '');
  return `${value < 0n ? '-' : ''}${abs / SCALE}${fraction ? `.${fraction}` : ''}`;
};
export type UsRealizedView = { state: 'loading' | 'unavailable' | 'ready';
  amount: string | null; net: boolean; title: string };
const unavailable = (reason: string): UsRealizedView => ({ state: 'unavailable', amount: null, net: false,
  title: `Polymarket US realized P&L unavailable: ${reason}` });

/** Use only current own-account snapshots. No history fetch, recent-fill sum,
 * position filter or market clock belongs in this whole-ledger total. */
export function usRealizedOf(read: UsTrackerRead | null): UsRealizedView {
  const loading: UsRealizedView = { state: 'loading', amount: null, net: false, title: 'Polymarket US realized P&L is loading' };
  if (!read || read.list.state === 'loading') return loading;
  if (read.list.state === 'failed') return unavailable(read.list.code);
  if (!read.list.complete) return unavailable('account list incomplete');
  if (!read.ownAccounts) return unavailable('no own-view account');
  if (new Set(read.accounts.map(a => a.accountId)).size !== read.accounts.length) return unavailable('duplicate account');
  if (read.accounts.length !== read.ownAccounts) return loading;
  let gross = 0n, net = 0n, fees = 0n, netKnown = true;
  for (const account of read.accounts) {
    if (account.openError || account.readError) return unavailable(account.openError ?? account.readError!);
    if (account.opening || !account.context || !account.snapshot) return loading;
    if (account.context.accountId !== account.accountId || !account.context.permissions.viewOwn
      || !isSameUsScope(account.context.scope, account.snapshot.scope)
      || account.snapshot.readDetails?.view !== 'own') return unavailable('account scope changed');
    if (account.snapshot.readDetails.inventory.state !== 'complete') return unavailable('ledger history incomplete');
    const summary = account.snapshot.realized;
    if (!summary || summary.grossUsd === null) return unavailable(summary?.reasons.join('; ') || 'complete accounting not supplied by relay');
    try {
      gross += units(summary.grossUsd);
      if (summary.netUsd === null || summary.feesUsd === null) netKnown = false;
      else { net += units(summary.netUsd); fees += units(summary.feesUsd); }
    } catch { return unavailable('invalid accounting amount'); }
  }
  return { state: 'ready', amount: decimal(netKnown ? net : gross), net: netKnown,
    title: netKnown ? `Net Polymarket US realized P&L after ${formatUsUsd(decimal(fees))} in recorded realized fees`
      : 'Gross Polymarket US realized P&L; recorded fee data is incomplete' };
}

/** Third venue in the existing tile, including its hide-P&L convention. */
export function UsRealizedValue({ view, showPnl }: { view: UsRealizedView; showPnl: boolean }) {
  return <div className="flex min-w-0 items-baseline gap-1" title={showPnl ? view.title : 'Polymarket US realized P&L hidden'}>
    <span className="text-muted-foreground text-[10px]">US{view.state === 'ready' ? ` ${view.net ? 'net' : 'gross'}` : ''}</span>
    <span data-testid="tracker-realized-us" className={`text-lg font-semibold ${!showPnl || view.amount === null
      ? 'text-muted-foreground' : view.amount.startsWith('-') ? 'text-negative' : 'text-positive'}`}>
      {!showPnl ? '•••' : view.amount === null ? '—' : formatUsSignedUsd(view.amount)}
    </span>
  </div>;
}
